import AbstractModule from './AbstractModule';

import Dropdown from './Dropdown';

import tap from '../ractive/ractive-events-tap';
import fade from '../ractive/ractive-transitions-fade';

import {unescapeHtml} from '../utils/html';
import {navigateTo} from '../global/pageTransitions';
import {scrollTo} from '../global/scrollTo';

export default class extends AbstractModule {
    constructor(options) {
        var id  = 'js-search';
        options = $.extend({}, {
            el : document.getElementById(id),
            $el: $('#' + id)
        }, options);

        super(options);

        this.openClass = 'has-search-main-open';
        this.maxWidth  = 1199;
        this.minWidth  = 1200;
        this.dropdown  = null;

        this.$document.on('toggleSearch.Search', (event, value) => {
            this.toggleSearch(value);
        });

        this.searchResultsController = this.initSearchResultsController();

        let params = this.queryString();
        if (params.keyword) {
            this.toggleSearch();
            let s = this.searchResultsController;
            s.set('keyword', params.keyword);
        }
    }

    /**
     * This controller is used for loading search results and various visual effects
     * @return  {Ractive Object}  Ractive instance
     */
    initSearchResultsController() {
        var _this       = this;
        var timeout     = 0;
        var minLength   = 3;
        var searchDelay = 500;
        var controller  = new Ractive({
            el           : this.$el,
            template     : '#js-search-template',
            data         : {
                events    : [],
                keyword   : '',
                locations : [],
                news      : [],
                objects   : [
                    'events',
                    'news',
                    'pages',
                    'locations'
                ],
                pages     : [],
                state     : 'inert',
                tips      : window.templateData.tips
            },
            computed     : {
                displaySearchResults: function () {
                    return this.get('keyword').length >= minLength;
                },
                encodedKeyword      : function () {
                    return this.get('keyword').replace(/\s/g, '&nbsp;');
                },
                hasEvents           : function () {
                    return this.objectCount('events') !== 0;
                },
                hasLocations        : function () {
                    return this.objectCount('locations') !== 0;
                },
                hasNews             : function () {
                    return this.objectCount('news') !== 0;
                },
                hasPages            : function () {
                    return this.objectCount('pages') !== 0;
                },
                hasResults          : function () {
                    return this.objectCount('events') !== 0 || this.objectCount('locations') !== 0 || this.objectCount('news') !== 0 || this.objectCount('pages') !== 0;
                },
                totalResults        : function () {
                    return this.objectCount('events') + this.objectCount('locations') + this.objectCount('news') + this.objectCount('pages');
                }
            },
            partials     : {
                img: '<img class="c-search-results_image" src="{{image}}" alt="">'
            },
            transitions  : {fade},
            events       : {tap},
            /**
             * Event model
             * @param  {object}  params  Initial values for the model
             * @return {object}          Event model
             *
             * Model properties
             *
             * @param {string}  url
             * @param {string}  icon
             * @param {string}  category
             * @param {string}  title
             * @param {string}  state
             * @param {string}  date
             * @param {string}  dateFormatted
             */
            getEventModel: function (params) {
                var defaults = {
                    url          : '',
                    icon         : '',
                    category     : '',
                    title        : '',
                    state        : '',
                    date         : '',
                    dateFormatted: ''
                };
                return $.extend(defaults, params);
            },
            /**
             * News model
             * @param  {object}  params  Initial values for the model
             * @return {object}          News model
             *
             * Model properties
             *
             * @param {string}  url
             * @param {string}  image
             * @param {string}  category
             * @param {string}  date
             * @param {string}  dateFormatted
             * @param {string}  title
             */
            getNewsModel : function (params) {
                var defaults = {
                    url          : '',
                    image        : '',
                    category     : '',
                    date         : '',
                    dateFormatted: '',
                    title        : ''
                };
                return $.extend(defaults, params);
            },
            /**
             * Page model
             * @param  {object}  params  Initial values for the model
             * @return {object}          Page model
             *
             * Model properties
             *
             * @param {string}  url
             * @param {string}  title
             * @param {string}  parent
             */
            getPageModel : function (params) {
                var defaults = {
                    url   : '',
                    title : '',
                    parent: ''
                };
                return $.extend(defaults, params);
            },
            /**
             * Count objects according to keypath
             * @param  {string}  keypath
             * @return {int}
             */
            objectCount  : function (keypath) {
                return this.get(keypath).length;
            },
            /**
             * Allows us to set proxy events and run other tasks when controller is initialized
             * @param  {array}  options  Array of options
             */
            oninit       : function (options) {
                this.on({
                    closeSearch         : function (event) {
                        _this.toggleSearch(false);
                    },
                    /**
                     * Empty all search results
                     * @param  {object}  event  Ractive event object
                     */
                    emptyResults        : function (event) {
                        this.set({
                            events    : [],
                            locations : [],
                            news      : [],
                            pages     : []
                        })
                    },
                    /**
                     * Fired by the input observer
                     * @param  {object}  event  Ractive event object
                     */
                    loadSearchResults   : function (event) {
                        var parameters = {
                            keyword: this.get('keyword'),
                            lang   : document.documentElement.lang
                        };

                        this.set('state', 'loading');
                        let location = window.location;
                        let url      = location.href;
                        let pIndex   = url.indexOf('?');

                        if (pIndex !== -1) {
                            url = url.slice(0, pIndex);
                        }

                        let params = _this.queryString();

                        params.keyword = parameters.keyword;
                        params         = $.param(params, true);
                        url            = url + "?" + params;

                        window.history.pushState({}, 'search', url);

                        _this.invokeLoadResults(parameters, (response) => {
                            setTimeout(() => {
                                this.set({
                                    events    : response.events,
                                    locations : response.locations,
                                    news      : response.news,
                                    pages     : response.pages
                                }).then(() => {
                                    this.set('state', 'inert');
                                });
                            }, 600);
                        });
                    },
                    /**
                     * Communicate with Smoothstate to navigate to the item's URL
                     * @param  {object}  event  Ractive event object
                     */
                    navigateTo          : function (event) {
                        event.original.preventDefault();

                        if (this.get('state') !== 'loading') {
                            navigateTo(this.get(event.keypath).url);
                        }
                    },
                    openSearchFromMobile: function (event) {
                        _this.toggleSearch(true);
                        setTimeout(() => {
                            _this.$input.focus();
                        }, 800);
                    },
                    /**
                     * Fill the keyword with the selected tip's value
                     * @param  {object}  event  Ractive event object
                     */
                    selectTip           : function (event) {
                        if (this.get('state') !== 'loading') {
                            this.set('keyword', this.get(event.keypath).label);
                        }
                    },
                    /**
                     * Gobble up the submit event
                     * @param  {object}  event  Ractive event object
                     */
                    submitForm          : function (event) {
                        event.original.preventDefault();
                        this.set('keyword', this.get('keyword'));
                    }
                });
            }
        });

        // Watching for changes on the typed in data
        controller.observe('keyword', function (newValue) {
            // Timeout clearing!
            if (timeout !== 0) {
                clearTimeout(timeout);
                timeout = 0;
            }

            // If less than minLength characters, clear out the search results
            if (newValue.length < minLength) {
                controller.fire('emptyResults');
            } else {
                // Dont search if loading
                if (controller.get('state') === 'inert') {
                    timeout = setTimeout(function () {
                        controller.fire('loadSearchResults');
                    }, searchDelay)
                }
            }
        });

        this.$input = this.$el.find('.js-search-input');

        // Constructing the Dropdown behaviour here since
        // it would get gobbled up by Ractive
        this.dropdown = new Dropdown({
            $el                : this.$el,
            el                 : this.el,
            'dropdown-maxWidth': this.maxWidth
        });

        return controller;
    }

    queryString() {
        var pairs = location.search.slice(1).split('&');

        var result = {};
        pairs.forEach(function (pair) {
            pair = pair.split('=');
            if (pair[1]) {
                result[pair[0]] = decodeURIComponent(pair[1] || '');
            }
        });

        return JSON.parse(JSON.stringify(result));
    }

    /**
     * Query server for search results and send them to the callback function
     * @param  {array}     data      Object formed by a keyword
     * @param  {function}  callback  Function to execute after the AJAX request is done
     */
    invokeLoadResults(data, callback) {
        // Default response
        var _response = {
            events    : [],
            locations : [],
            news      : [],
            pages     : []
        };

        var jqxhr = $.ajax({
            method: 'GET',
            url   : this.$el.find('[action]').attr('action'),
            data  : data
        })
            .done((response) => {
                if (response.success === true) {
                    _response = response;
                }
            })
            .fail(() => {
                console.log('error');
            })
            .always(() => {
                callback(_response);
            });
    }

    /**
     * Toggles class on body that determines if search is visible or not
     * @param  boolean|mixed  State
     */
    toggleSearch(state) {
        if (typeof state !== 'undefined' && typeof state === 'boolean') {
            this.$body.toggleClass(this.openClass, state);
        } else {
            this.$body.toggleClass(this.openClass);
        }

        if (state === false) {
            this.$input.blur();
            //     this.searchResultsController.set('keyword', '');
        }


        if (!window.matchMedia('(min-width: ' + this.minWidth + 'px)').matches) {
            return;
        }

        if (this.$body.hasClass(this.openClass)) {
            this.$input.focus();
        }
    }

    destroy() {
        this.dropdown.destroy();
        this.searchResultsController.teardown();
        this.$document.off('.Search');
        this.$el.off('.Search');
    }
}
